/**
 * Acciones del panel de detalle (§7.3): editar, descartar, profundizar,
 * verificar fuentes y marcar como duplicado de otro registro.
 *
 * Cada una confirma o avisa según su reversibilidad:
 *   - descartar     reversible, avisa con botón de deshacer
 *   - fusionar      pide confirmación: no hay deshacer en la interfaz
 *   - borrado duro  pide confirmación y advierte que es permanente
 *   - profundizar   informa que la corrida quedó lanzada y sigue en segundo plano
 */

import {
  GitMerge,
  PencilLine,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Trash2,
  Trash,
} from 'lucide-react'
import { useState } from 'react'

import {
  useBorrarStakeholder,
  useDescartarStakeholder,
  useFusionar,
  useProfundizarStakeholder,
  useRestaurarStakeholder,
  useVerificarFuentes,
} from '../../api/hooks'
import type { Corrida, Stakeholder, StakeholderFila } from '../../api/tipos'
import { mensajeDeError, useAvisos } from '../../ui/avisos-contexto'
import { Confirmacion, Dialogo } from '../../ui/Dialogo'
import { Boton, Chip } from '../../ui/primitivos'
import { EditorFicha } from './EditorFicha'
import { SelectorStakeholder } from './SelectorStakeholder'

export function AccionesFicha({
  ficha,
  onCorridaLanzada,
}: {
  ficha: Stakeholder
  /** Para que el workspace muestre el progreso de la corrida. */
  onCorridaLanzada?: (corrida: Corrida) => void
}) {
  const avisos = useAvisos()
  const [editando, setEditando] = useState(false)
  const [confirmarBorrado, setConfirmarBorrado] = useState(false)
  const [fusionando, setFusionando] = useState(false)
  const [destino, setDestino] = useState<StakeholderFila | null>(null)

  const descartar = useDescartarStakeholder(ficha.entity)
  const restaurar = useRestaurarStakeholder(ficha.entity)
  const borrar = useBorrarStakeholder(ficha.entity)
  const profundizar = useProfundizarStakeholder(ficha.entity)
  const verificar = useVerificarFuentes(ficha.entity)
  const fusionar = useFusionar(ficha.entity)

  const descartado = ficha.estado === 'DESCARTADO'

  function alDescartar() {
    descartar.mutate(
      { id: ficha.id },
      {
        onSuccess: () =>
          avisos.exito(`«${ficha.nombre}» descartado`, {
            detalle: 'Desapareció del grafo y de la tabla. Nada se borró.',
            deshacer: {
              etiqueta: 'Restaurar',
              fn: () => restaurar.mutate(ficha.id),
            },
          }),
        onError: (error) => avisos.error('No se pudo descartar', mensajeDeError(error)),
      },
    )
  }

  function alProfundizar() {
    profundizar.mutate(
      { id: ficha.id },
      {
        onSuccess: (corrida) => {
          if (corrida.reutilizada) {
            avisos.info(
              'Ya había una profundización reciente sobre este actor; se reutilizó sin gastar créditos.',
            )
          } else {
            avisos.exito('Profundización lanzada', {
              detalle:
                'Pide solo información nueva sobre este actor. Puede tardar varios minutos; el resultado se integra solo.',
            })
          }
          onCorridaLanzada?.(corrida)
        },
        onError: (error) =>
          avisos.error('No se pudo lanzar la profundización', mensajeDeError(error)),
      },
    )
  }

  function alVerificar() {
    verificar.mutate(ficha.id, {
      onSuccess: (resultado) =>
        avisos.exito(
          resultado.nuevas > 0
            ? `${resultado.nuevas} fuente${resultado.nuevas === 1 ? '' : 's'} nueva${resultado.nuevas === 1 ? '' : 's'}`
            : 'Sin fuentes nuevas',
          {
            detalle: `Se revisaron ${resultado.encontradas} resultados de búsqueda para este actor.`,
          },
        ),
      onError: (error) => avisos.error('No se pudo verificar', mensajeDeError(error)),
    })
  }

  function alFusionar() {
    if (!destino) return
    fusionar.mutate(
      { destinoId: destino.id, origenIds: [ficha.id] },
      {
        onSuccess: () => {
          avisos.exito(`Fusionado en «${destino.nombre}»`, {
            detalle:
              'Se unieron listas y fuentes, y se conservó el nivel más alto de cada uno.',
          })
          setFusionando(false)
          setDestino(null)
        },
        onError: (error) => avisos.error('No se pudo fusionar', mensajeDeError(error)),
      },
    )
  }

  return (
    <>
      <Boton
        variante="secundario"
        icono={<PencilLine size={13} />}
        onClick={() => setEditando(true)}
      >
        Editar
      </Boton>

      <Boton
        variante="secundario"
        icono={<Sparkles size={13} />}
        cargando={profundizar.isPending}
        onClick={alProfundizar}
        title="Pide al proveedor solo información nueva sobre este actor"
      >
        Profundizar
      </Boton>

      <Boton
        variante="secundario"
        icono={<ShieldCheck size={13} />}
        cargando={verificar.isPending}
        onClick={alVerificar}
        title="Busca evidencia adicional sin gastar una corrida completa"
      >
        Verificar fuentes
      </Boton>

      <Boton
        variante="secundario"
        icono={<GitMerge size={13} />}
        onClick={() => setFusionando(true)}
      >
        Marcar como duplicado de…
      </Boton>

      {descartado ? (
        <Boton
          variante="secundario"
          icono={<RotateCcw size={13} />}
          cargando={restaurar.isPending}
          onClick={() =>
            restaurar.mutate(ficha.id, {
              onSuccess: () => avisos.exito(`«${ficha.nombre}» restaurado`),
              onError: (error) => avisos.error('No se pudo restaurar', mensajeDeError(error)),
            })
          }
        >
          Restaurar
        </Boton>
      ) : (
        <Boton
          variante="peligro"
          icono={<Trash2 size={13} />}
          cargando={descartar.isPending}
          onClick={alDescartar}
          title="Lo quita de la vista de forma reversible"
        >
          Descartar
        </Boton>
      )}

      {descartado && (
        <Boton
          variante="peligro"
          icono={<Trash size={13} />}
          onClick={() => setConfirmarBorrado(true)}
          title="Borrado permanente e irreversible"
        >
          Borrar
        </Boton>
      )}

      {editando && <EditorFicha ficha={ficha} abierto onCerrar={() => setEditando(false)} />}

      <Confirmacion
        abierto={confirmarBorrado}
        onCerrar={() => setConfirmarBorrado(false)}
        onConfirmar={() =>
          borrar.mutate(ficha.id, {
            onSuccess: () => {
              avisos.exito(`«${ficha.nombre}» borrado permanentemente`)
              setConfirmarBorrado(false)
            },
            onError: (error) => avisos.error('No se pudo borrar', mensajeDeError(error)),
          })
        }
        titulo="Borrar permanentemente"
        descripcion={`«${ficha.nombre}» se eliminará de la base de datos junto con sus vínculos. Esta acción no se puede deshacer.`}
        detalle={
          <p className="text-[12px] text-[var(--color-texto-suave)]">
            Si solo quieres quitarlo de la vista, usa <strong>Descartar</strong>: es reversible
            y queda registrado en la auditoría.
          </p>
        }
        etiquetaConfirmar="Borrar para siempre"
        peligro
        cargando={borrar.isPending}
      />

      <Dialogo
        abierto={fusionando}
        onCerrar={() => {
          setFusionando(false)
          setDestino(null)
        }}
        titulo="Marcar como duplicado de…"
        descripcion={`«${ficha.nombre}» se fusionará en el registro que elijas, que conservará su nombre.`}
        pie={
          <>
            <Boton
              variante="secundario"
              onClick={() => {
                setFusionando(false)
                setDestino(null)
              }}
              disabled={fusionar.isPending}
            >
              Cancelar
            </Boton>
            <Boton
              variante="primario"
              icono={<GitMerge size={13} />}
              disabled={!destino}
              cargando={fusionar.isPending}
              onClick={alFusionar}
            >
              Fusionar
            </Boton>
          </>
        }
      >
        {/* Solo se monta con el diálogo abierto: si no, su búsqueda dispara
            una consulta a la tabla cada vez que se abre una ficha. */}
        {fusionando && (
          <SelectorStakeholder
            entidadId={ficha.entity}
            excluir={[ficha.id]}
            seleccionado={destino?.id ?? null}
            onSeleccionar={setDestino}
          />
        )}
        {destino && (
          <p className="mt-3 rounded-md border border-[var(--color-borde)] bg-[var(--color-lienzo)] p-2.5 text-[12px]">
            Se conservará <Chip>{destino.nombre}</Chip> y «{ficha.nombre}» pasará a ser uno de
            sus alias. Las listas y fuentes de ambos se unen; en los niveles se queda el más
            alto y la diferencia queda registrada.
          </p>
        )}
      </Dialogo>
    </>
  )
}
